<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getOrganisationStatistics, type OrganisationsStatistics } from '@/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsNumber,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';

dayjs.extend(relativeTime);

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<OrganisationsStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    {
      label: 'Avg users / organisation',
      value: formatStatisticsNumber(data.value.average_users_per_organisation),
    },
  ];
});

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getOrganisationStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load organisation statistics');
    logger.error('Failed to load organisation statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <n-flex v-if="data !== undefined" vertical :size="16">
      <statistics-kpi-grid :tiles="kpiTiles" />

      <n-card title="Newest organisations" size="small">
        <n-table size="small" :single-line="false">
          <thead>
            <tr>
              <th>Name</th>
              <th>Allowed credits</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody v-if="data.newest_organisations.length > 0">
            <tr v-for="org in data.newest_organisations" :key="org.uid">
              <td>{{ org.name }}</td>
              <td>
                {{ org.allowed_insights_credits }} / {{ org.allowed_insights_credits_period }}
              </td>
              <td>
                <n-text depth="3">{{ dayjs(org.created_at).fromNow() }}</n-text>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="3">
                <n-empty description="No organisations found" />
              </td>
            </tr>
          </tbody>
        </n-table>
      </n-card>
    </n-flex>
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
